// src/components/About.jsx
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { t as i18n } from "../i18n";

export default function About({
  isOpen = false,
  onClose = () => {},
  theme = "light",
  language = "en",
}) {
  const dialogRef = useRef(null);
  const msg = i18n(language);

  // Lock/unlock body scroll (hook always called; guarded inside)
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Focus + ESC (hook always called; guarded inside)
  useEffect(() => {
    if (!isOpen) return;
    dialogRef.current?.focus({ preventScroll: true });
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Build overlay
  const overlay = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="about-title"
      tabIndex={-1}
      ref={dialogRef}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        ...overlayBase,
        ...(theme === "dark" ? overlayDark : overlayLight),
      }}
    >
      <style>{modalCSS(theme)}</style>

      <div className="about-card modal-card" onClick={(e) => e.stopPropagation()}>
        <button aria-label={msg.close || "Close"} className="modal-close" onClick={onClose}>
          ×
        </button>

        <h1 id="about-title" className="about-title">{msg.about_title}</h1>
        <p className="about-subtitle">{msg.about_intro}</p>

        <div className="about-section">
          <h3>{msg.about_what_title}</h3>
          <p>{msg.about_what_body}</p>
        </div>

        <div className="about-section">
          <h3>{msg.about_status_title}</h3>
          <p>{msg.about_status_body}</p>
        </div>

        <div className="about-section">
          <h3>{msg.about_affiliation_title}</h3>
          <p>{msg.about_affiliation_body}</p>
        </div>

        <div className="about-section">
          <h3>{msg.about_contact_title}</h3>
          <p>
            {msg.about_contact_body}{" "}
            <a href="mailto:nammametrolive@gmail.com">nammametrolive@gmail.com</a>
          </p>
        </div>
      </div>
    </div>
  );

  return isOpen ? createPortal(overlay, document.body) : null;
}

const overlayBase = {
  position: "fixed",
  inset: 0,
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "64px 16px 24px",
  zIndex: 10000,
  pointerEvents: "auto",
  overflowY: "auto",
  scrollbarGutter: "stable both-edges",
};
const overlayLight = { background: "rgba(10,15,25,0.25)" };
const overlayDark  = { background: "rgba(0,0,0,0.35)" };

const modalCSS = (theme) => `
  .about-title {
    font-size: 2.1rem; font-weight: 800; margin-bottom: 14px;
    color: ${theme === "dark" ? "#f3f4f6" : "#1f2937"};
    letter-spacing: .2px;
  }
  .about-subtitle {
    font-size: 1.05rem;
    color: ${theme === "dark" ? "#cbd5e1" : "#4b5563"};
    margin-bottom: 22px;
    max-width: 100%;                 /* <— no artificial wrap limit */
  }
  .about-card {
    background-color: ${theme === "dark" ? "#111827" : "#ffffff"};
    border-radius: 16px; padding: 28px 28px 26px;
    box-shadow: ${theme === "dark"
      ? "0 24px 60px rgba(0,0,0,0.45)"
      : "0 20px 50px rgba(0,0,0,0.25)"};
    width: min(920px, 100%); position: relative;
    animation: modalPop 220ms cubic-bezier(.2,.8,.2,1);
    border: 1px solid ${theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"};
  }
  .about-section { margin-bottom: 16px; }
  .about-section h3 {
    font-size: 1.1rem; margin-bottom: 8px;
    color: ${theme === "dark" ? "#80d8ff" : "#1f3c88"};
  }
  .about-section p {
    font-size: 1rem; line-height: 1.6;
    color: ${theme === "dark" ? "#e5e7eb" : "#374151"};
    max-width: 100%;                 /* <— ensure paragraphs span the card */
  }

  /* Remove hover transforms that cause subtle “shake” on leave */
  .about-card:hover { transform: none; }
  .modal-close {
    position: absolute; top: 10px; right: 12px; height: 36px; width: 36px;
    border-radius: 999px; border: 1px solid ${theme === "dark"
      ? "rgba(255,255,255,0.08)"
      : "rgba(0,0,0,0.08)"};
    background: ${theme === "dark" ? "rgba(31,41,55,0.7)" : "rgba(255,255,255,0.8)"};
    color: ${theme === "dark" ? "#e5e7eb" : "#111827"};
    font-size: 20px; line-height: 1; display: inline-flex; align-items: center; justify-content: center;
    cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,0.06);
    transition: background .2s ease, box-shadow .2s ease; /* no translate */
  }
  .modal-close:hover { background: ${theme === "dark" ? "#1f2937" : "#ffffff"}; }

  @keyframes modalPop {
    0% { transform: translateY(6px) scale(.98); opacity: 0; }
    100% { transform: translateY(0) scale(1); opacity: 1; }
  }
`;
